import { useLazyQuery } from '@apollo/client/react';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Box, Button, Grid, Icon } from '~/components/blocks';
import { MessageList, ScheduledMessageList } from '~/components/partials';
import {
  GetWebVisitorMessagesDocument,
  GetWebVisitorMessagesQuery,
  ScheduledWebVisitorMessageFragment,
  ScheduledWebVisitorMessagesFragment,
  WebVisitorFollowupMessageFragment,
  WebVisitorMessagesFragment,
} from '~/graphql/generated/graphql';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { Panel, PanelType } from '~/state/partials/message_modal/types';
import {
  patientDetailDialogMessageState,
  patientDetailDialogState,
} from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';

type Props = {
  webVisitorId: string;
};

const ButtonWrapper = styled('div')(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.bg,
    borderRadius: theme.radii.default,
    padding: theme.space.l,
    margin: theme.space.xs,
  }),
);

const isWebVisitorMessages = (alias: 'all' | 'scheduled', data?: GetWebVisitorMessagesQuery) => {
  if (!data || data?.all?.__typename !== 'WebVisitor') {
    return undefined;
  }

  if (alias === 'scheduled') {
    return data.scheduled as ScheduledWebVisitorMessagesFragment;
  } else {
    return data.all as WebVisitorMessagesFragment;
  }
};

export const MessagePane = React.memo((props: Props) => {
  const { webVisitorId } = props;
  const theme = useTheme();

  const { verifyRole } = usePractitionerRole();
  const isVerified = verifyRole('pharmacist');
  const setPatientDetailDialogMessageState = useSetAtom(patientDetailDialogMessageState);
  const setPatientDetailDialogState = useSetAtom(patientDetailDialogState);

  const [getMessage, { loading, data }] = useLazyQuery(GetWebVisitorMessagesDocument);
  const messages = useMemo(() => {
    return (
      isWebVisitorMessages('all', data)?.medicationFollowupMessages.nodes.map(
        (node) => node as WebVisitorFollowupMessageFragment,
      ) || []
    );
  }, [data]);
  const scheduledMessages = useMemo(() => {
    return (
      isWebVisitorMessages('scheduled', data)?.medicationFollowupMessages.nodes.map(
        (node) => node as ScheduledWebVisitorMessageFragment,
      ) || []
    );
  }, [data]);

  const handleClick = useCallback(
    (_panel: PanelType, messageId?: string) => {
      setPatientDetailDialogMessageState((_state) => ({
        ..._state,
        selectedMessageId: messageId || null,
      }));
      setPatientDetailDialogState((_state) => ({
        ..._state,
        isOpen: true,
        type: 'message' as TabType,
        patientId: webVisitorId,
      }));
    },
    [webVisitorId, setPatientDetailDialogMessageState, setPatientDetailDialogState],
  );

  useEffect(() => {
    getMessage({
      variables: {
        id: webVisitorId,
      },
    });
  }, [getMessage, webVisitorId]);

  return (
    <Box position="relative" height="100%" overflow="auto" padding={theme.space.l}>
      <Grid height="100%" gridTemplateRows="min-content min-content 1fr" gridTemplateColumns="1fr">
        {isVerified ? (
          <ButtonWrapper>
            <Button wide="fill" use="base" onClick={() => handleClick(Panel.initial)}>
              <Icon icon="comment" color="white" size="l" />
              患者にメッセージ送信
            </Button>
          </ButtonWrapper>
        ) : (
          <Box />
        )}
        <Box marginTop={theme.space.l}>
          {scheduledMessages.length > 0 && (
            <ScheduledMessageList scheduledMessages={scheduledMessages} onClick={handleClick} />
          )}
        </Box>
        <Box marginTop={theme.space.l} height="100%" overflow="auto">
          <MessageList loading={loading} messages={messages} onClick={handleClick} />
        </Box>
      </Grid>
    </Box>
  );
});

MessagePane.displayName = 'MessagePane';
